import React, { useMemo, useState } from 'react';

import { Box, Button, Dialog, DialogActions, DialogContent, DialogTitle, Typography } from '@mui/material';
import Grid2 from '@mui/material/Grid2';
import { DataGrid, GridColDef, GridPaginationModel, GridSortDirection, GridSortModel } from '@mui/x-data-grid';

import { Code } from '~/components/Common/Code';

import { formatDateOrdinalWithClock } from '~/services/TimeService';

interface Props {
  onPaginationModelChange: (model: GridPaginationModel) => void;
  paginationModel: GridPaginationModel;
  rows: Array<App.BusinessActivityLog>;
  total: number;
  onSortChange?: (field: string, direction: string) => void;
  sort?: string;
  sortDirection?: string;
}

export default function ActivityLogGrid({
  onPaginationModelChange,
  paginationModel,
  rows,
  total,
  onSortChange,
  sort = 'createdAt',
  sortDirection = 'desc',
}: Props) {
  const [openRow, setOpenRow] = useState<App.BusinessActivityLog | null>(null);

  const sortModel: GridSortModel = [
    {
      field: sort,
      sort: sortDirection as GridSortDirection,
    },
  ];

  const columns: Array<GridColDef<App.BusinessActivityLog>> = useMemo(() => {
    return [
      {
        field: 'createdAt',
        headerName: 'Created At',
        display: 'flex',
        width: 180,
        valueFormatter: formatDateOrdinalWithClock,
      },
      { field: 'message', headerName: 'Message', flex: 1, display: 'flex' },
      { field: 'resource', headerName: 'Resource', width: 180, display: 'flex' },
      { field: 'operation', headerName: 'Operation', width: 120, display: 'flex' },
      {
        field: 'details',
        headerName: 'Details',
        display: 'flex',
        width: 100,
        renderCell: (params) => (
          <Button size="small" variant="outlined" onClick={() => setOpenRow(params.row)}>
            View
          </Button>
        ),
      },
    ];
  }, []);

  const handleSortModelChange = (newSortModel: GridSortModel) => {
    if (newSortModel.length > 0 && onSortChange) {
      const { field, sort: direction } = newSortModel[0];
      onSortChange(field, direction || 'asc');
    }
  };

  const handleCloseModal = () => {
    setOpenRow(null);
  };

  return (
    <>
      <Grid2 size={12}>
        <Box width="100%" height={600}>
          <DataGrid
            columns={columns}
            onPaginationModelChange={onPaginationModelChange}
            onSortModelChange={handleSortModelChange}
            paginationMode="server"
            paginationModel={paginationModel}
            rows={rows || []}
            rowCount={total}
            sortModel={sortModel}
            sortingMode="server"
          />
        </Box>
      </Grid2>
      <Dialog open={!!openRow} onClose={handleCloseModal} maxWidth="md" fullWidth>
        <DialogTitle>Activity Details</DialogTitle>
        <DialogContent>
          <Typography variant="h6" gutterBottom>
            Message
          </Typography>
          <Typography variant="body1" paragraph sx={{ mb: 3 }}>
            {openRow?.message}
          </Typography>
          <Typography variant="h6" gutterBottom>
            Details
          </Typography>
          <Code value={JSON.stringify(openRow?.details, null, 2)} lang="json" />
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseModal}>Close</Button>
        </DialogActions>
      </Dialog>
    </>
  );
}
